import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, List, ListItem, IconButton, ListItemText, Button, Alert } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { waybillPackageSizesTemplate } from 'apollo/templates/organization';
import AddPackageSizeDialog from './add-package-size-dialog.components';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { deleteWaybillPackageSizeTemplate } from 'apollo/templates/organization/mutation.graphql';
import { WaybillPackageSize } from 'models/transport';

const WaybillPackageSizes: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
    const [deleteSizeId, setDeleteSizeId] = useState<string>();

    const {
        data: { waybillPackageSizes } = {},
        loading,
        refetch,
    } = useQuery(waybillPackageSizesTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteWaybillPackageSize] = useMutation(deleteWaybillPackageSizeTemplate, {
        onCompleted: () => {
            refetch();
            setDeleteSizeId(undefined);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleSizeAdd = (_size: WaybillPackageSize) => {
        refetch();
        setIsAddDialogOpen(false);
    };

    const handleDeleteClick = (sizeId?: string) => {
        if (sizeId) {
            setDeleteSizeId(sizeId);
            setIsDeleteConfirmDialogOpen(true);
        }
    };

    const handleDeleteSize = () => {
        console.log(deleteSizeId);
        deleteWaybillPackageSize({
            variables: {
                packageSizeId: deleteSizeId,
            },
        });
        setIsDeleteConfirmDialogOpen(false);
    };

    return (
        <Fragment>
            {waybillPackageSizes?.length > 0 ? (
                <Paper variant="outlined">
                    <List dense>
                        {waybillPackageSizes?.map((size: WaybillPackageSize) => (
                            <ListItem
                                key={size._id}
                                secondaryAction={
                                    <IconButton onClick={() => handleDeleteClick(size._id)} edge="end">
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemText
                                    primary={size.name}
                                    secondary={`${size.width}cm x ${size.height}cm x ${size.length}cm`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ) : (
                <Alert severity="info">{translate('lab-settings:NO_PACKAGE_SIZES')}</Alert>
            )}

            <Box mt={1} mb={3}>
                <Button onClick={() => setIsAddDialogOpen(true)} startIcon={<AddIcon />}>
                    {translate('lab-settings:ADD_PACKAGE_SIZE')}
                </Button>
            </Box>
            <AddPackageSizeDialog
                open={isAddDialogOpen}
                onDialogClose={() => setIsAddDialogOpen(false)}
                onAdd={handleSizeAdd}
            />
            <ConfirmDialog
                open={isDeleteConfirmDialogOpen}
                handleClose={() => setIsDeleteConfirmDialogOpen(false)}
                dialogContent={translate('lab-settings:CONFIRM_DELETE_PACKAGE_SIZE')}
                handleConfirm={handleDeleteSize}
            />
        </Fragment>
    );
};

export default WaybillPackageSizes;
